import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api/client';
import type { ClientDetail, Deliverable, DeliverableStatus } from '@pixerito/api-client';
import { Dialog, ErrorNotice, Field, Icon, Loading } from '../../components/ui';
import { useAsync } from '../../lib/hooks';
import { STATUS_LABEL, STATUS_ORDER } from '../../lib/format';
import { DeliverablesCenter } from '../client/ClientDeliverables';

type Editing =
  { mode: 'new' } | { mode: 'edit'; item: Deliverable } | { mode: 'version'; item: Deliverable } | { mode: 'delete'; item: Deliverable };

export function AdminDeliverables({ client, onToast }: { client: ClientDetail; onToast: (m: string) => void }) {
  const { data, setData, error, loading, reload } = useAsync(() => api.clientDeliverables(client.id), [client.id]);
  const [editing, setEditing] = useState<Editing | null>(null);
  const modules = client.modules.filter((m) => m.enabled && (m.type === 'brand' || m.type === 'video'));

  const replace = (d: Deliverable) => setData((list) => (list ?? []).map((x) => (x.id === d.id ? d : x)));

  async function changeStatus(d: Deliverable, status: DeliverableStatus) {
    try {
      replace(await api.updateDeliverable(d.id, { title: d.title, description: d.description, dueDate: d.dueDate, status }));
      onToast(`${d.title}: ${STATUS_LABEL[status].toLowerCase()}`);
    } catch (err) {
      onToast(err instanceof ApiError ? err.message : 'Não foi possível mudar o status.');
    }
  }

  if (loading && !data) return <Loading />;
  if (error) return <ErrorNotice message={error} onRetry={reload} />;

  const newButton = (
    <button className="btn btn--action btn--sm" onClick={() => setEditing({ mode: 'new' })}>
      <Icon name="plus" size={15} />
      Novo entregável
    </button>
  );

  return (
    <div className="stack-lg">
      {data && data.length > 0 && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          {newButton}
        </div>
      )}
      <DeliverablesCenter
        items={data ?? []}
        emptyAction={newButton}
        actions={(d) => (
          <div className="stack" style={{ gap: 6, justifyItems: 'end' }}>
            <label className="sr-only" htmlFor={`st-${d.id}`}>
              Status
            </label>
            <select
              id={`st-${d.id}`}
              className="select"
              style={{ minHeight: 34, padding: '4px 36px 4px 12px', fontSize: 13, width: 'auto' }}
              value={d.status}
              onChange={(e) => changeStatus(d, e.target.value as DeliverableStatus)}
            >
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <div className="row" style={{ gap: 4 }}>
              <button
                className="icon-btn"
                title="Adicionar versão"
                aria-label="Adicionar versão"
                onClick={() => setEditing({ mode: 'version', item: d })}
              >
                <Icon name="link" />
              </button>
              <button className="icon-btn" title="Editar" aria-label="Editar" onClick={() => setEditing({ mode: 'edit', item: d })}>
                <Icon name="edit" />
              </button>
              <button className="icon-btn" title="Excluir" aria-label="Excluir" onClick={() => setEditing({ mode: 'delete', item: d })}>
                <Icon name="trash" />
              </button>
            </div>
          </div>
        )}
      />

      {editing?.mode === 'new' || editing?.mode === 'edit' ? (
        <DeliverableDialog
          clientId={client.id}
          modules={modules.map((m) => m.type as 'brand' | 'video')}
          item={editing.mode === 'edit' ? editing.item : undefined}
          onClose={() => setEditing(null)}
          onSaved={(d, created) => {
            setData((list) => (created ? [d, ...(list ?? [])] : (list ?? []).map((x) => (x.id === d.id ? d : x))));
            setEditing(null);
            onToast(created ? 'Entregável criado' : 'Entregável salvo');
          }}
        />
      ) : null}
      {editing?.mode === 'version' && (
        <VersionDialog
          item={editing.item}
          onClose={() => setEditing(null)}
          onSaved={(d) => {
            replace(d);
            setEditing(null);
            onToast(`Versão ${d.latestVersion?.number} adicionada`);
          }}
        />
      )}
      {editing?.mode === 'delete' && (
        <Dialog title="Excluir entregável" onClose={() => setEditing(null)}>
          <p>Excluir “{editing.item.title}” com todas as versões? O cliente deixa de ver este item.</p>
          <div className="row">
            <button
              className="btn btn--danger"
              onClick={async () => {
                await api.deleteDeliverable(editing.item.id);
                setData((list) => (list ?? []).filter((x) => x.id !== editing.item.id));
                setEditing(null);
                onToast('Entregável excluído');
              }}
            >
              Excluir
            </button>
            <button className="btn btn--quiet" onClick={() => setEditing(null)}>
              Cancelar
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function DeliverableDialog({
  clientId,
  modules,
  item,
  onClose,
  onSaved,
}: {
  clientId: string;
  modules: ('brand' | 'video')[];
  item?: Deliverable;
  onClose: () => void;
  onSaved: (d: Deliverable, created: boolean) => void;
}) {
  const [form, setForm] = useState({
    module: item?.module ?? modules[0] ?? 'brand',
    title: item?.title ?? '',
    description: item?.description ?? '',
    dueDate: item?.dueDate ?? '',
    status: item?.status ?? ('in_production' as DeliverableStatus),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    const body = { ...form, description: form.description || null, dueDate: form.dueDate || null };
    try {
      const saved = item ? await api.updateDeliverable(item.id, body) : await api.createDeliverable(clientId, body);
      onSaved(saved, !item);
    } catch (err) {
      if (err instanceof ApiError) setErrors({ ...err.fields, _: err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={item ? 'Editar entregável' : 'Novo entregável'} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        {errors._ && <div className="notice notice--error">{errors._}</div>}
        {!item && modules.length > 1 && (
          <Field label="Serviço" htmlFor="d-module" error={errors.module}>
            <select
              id="d-module"
              className="select"
              value={form.module}
              onChange={(e) => setForm({ ...form, module: e.target.value as 'brand' | 'video' })}
            >
              <option value="brand">Identidade Visual</option>
              <option value="video">Vídeo</option>
            </select>
          </Field>
        )}
        <Field label="Nome" htmlFor="d-title" error={errors.title}>
          <input
            id="d-title"
            className="input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Ex.: Manual da marca"
            autoFocus
          />
        </Field>
        <Field label="Descrição" htmlFor="d-desc" hint="Opcional. O cliente vê este texto.">
          <textarea
            id="d-desc"
            className="textarea"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
        <div className="grid-2">
          <Field label="Status" htmlFor="d-status" error={errors.status}>
            <select
              id="d-status"
              className="select"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as DeliverableStatus })}
            >
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Previsão de entrega" htmlFor="d-due" hint="Opcional.">
            <input
              id="d-due"
              className="input"
              type="date"
              value={form.dueDate ?? ''}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            />
          </Field>
        </div>
        <div className="row">
          <button className="btn btn--action" type="submit" disabled={busy}>
            {busy ? 'Salvando…' : item ? 'Salvar' : 'Criar entregável'}
          </button>
          <button className="btn btn--quiet" type="button" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function VersionDialog({ item, onClose, onSaved }: { item: Deliverable; onClose: () => void; onSaved: (d: Deliverable) => void }) {
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = (item.versions[0]?.number ?? 0) + 1;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.addVersion(item.id, url.trim(), notes.trim() || null));
    } catch (err) {
      setError(err instanceof ApiError ? (err.fields.fileUrl ?? err.message) : 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Versão ${next} · ${item.title}`} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <Field label="Link do arquivo" htmlFor="v-url" error={error} hint="Drive, WeTransfer, Frame.io… O cliente abre por este link.">
          <input
            id="v-url"
            className="input"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
            autoFocus
          />
        </Field>
        <Field label="Notas da versão" htmlFor="v-notes" hint="Opcional. Ex.: Ajustes da 2ª rodada.">
          <input id="v-notes" className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div className="row">
          <button className="btn btn--action" type="submit" disabled={busy || !url.trim()}>
            {busy ? 'Salvando…' : 'Adicionar versão'}
          </button>
          <button className="btn btn--quiet" type="button" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </form>
    </Dialog>
  );
}
